import { AnyAction } from 'redux'
import actionTypes from '../actions/actionTypes'
import gameState from '../../helpers/enums/gameStates'
import { IActionAnswerSelected, IActionTransitionEnded } from '../../redux/actions/questionActions'
import { IPlayerWithCurrentNumberOfCorrectAnswer } from '../../helpers/data/player'

interface ICurrentView {
	type: string
	payload: any
}

export interface IGameStore {
	participants: any[]
	activities: any[]
	currentView: ICurrentView
	settings: any[]
	_isLoaded: boolean
	studentsIDWithNumberOfCorrectAnswersFromServer: IPlayerWithCurrentNumberOfCorrectAnswer[] //number of FULL correct answers (a FULL correct answer is when a student selected all the correct answers of a question)
}

const defaultState = {
	participants: [],
	activities: [],
	currentView: {
		type: '',
		payload: null,
	},
	settings: [],
	_isLoaded: false,
	studentsIDWithNumberOfCorrectAnswersFromServer: [],
}

function updateGameState(state: IGameStore, action: AnyAction): IGameStore {
	if (action.payload.currentView && action.payload.currentView.type === 'game') {
		const oldParticipants: any = {}
		state.participants.forEach((participant: any) => {
			oldParticipants[participant.deviceUid] = participant
		})
		let oldParticipant
		const newParticipants = action.payload.participants.map((participant: any) => {
			if (participant.view && participant.view.type === 'game') {
				oldParticipant = oldParticipants[participant.deviceUid]
				if (
					oldParticipant &&
					oldParticipant.view.type === 'game' &&
					oldParticipant.view.payload.currentActivityUid === participant.view.payload.currentActivityUid
				) {
					const isGameAnswered = oldParticipant.view.payload.isGameAnswered || participant.view.payload.isGameAnswered
					const answersUids = oldParticipant.view.payload.answersUids || participant.view.payload.answersUids
					let answerState = gameState.WAITING

					if (oldParticipant.view.payload.state === gameState.TRANSITIONING) {
						answerState = gameState.TRANSITIONING
					} else if (participant.view.payload.state === gameState.ENDED) {
						answerState = gameState.ENDED
					} else if (isGameAnswered || (answersUids && answersUids[0])) {
						answerState = gameState.ANSWERED
					}
					return {
						...participant,
						view: {
							...participant.view,
							payload: {
								...participant.view.payload,
								answersUids,
								isGameAnswered,
								state: answerState,
							},
						},
					}
				} else {
					return participant
				}
			} else {
				return participant
			}
		})
		action.payload.participants = newParticipants
	}

	return {
		...state,
		...action.payload,
		_isLoaded: true,
	}
}

const setAnswerAsAnswered = (state: IGameStore, action: IActionAnswerSelected): IGameStore => {
	const participantIndex = state.participants.findIndex(
		(participant: any) => participant.deviceUid === action.playerUid
	)
	const participantData = state.participants[participantIndex]
	const participants = [
		...state.participants.slice(0, participantIndex),
		{
			...participantData,
			view: {
				...participantData.view,
				payload: {
					...participantData.view.payload,
					state: gameState.ANSWERED,
					isGameAnswered: true,
					answersUids: action.selectedAnswersIDs,
				},
			},
		},
		...state.participants.slice(participantIndex + 1),
	]
	return {
		...state,
		participants,
	}
}

const updateUserState = (state: IGameStore, action: IActionTransitionEnded) => {
	const participantIndex = state.participants.findIndex(
		(participant: any) => participant.deviceUid === action.playerUid
	)
	const participantData = state.participants[participantIndex]
	const participants = [
		...state.participants.slice(0, participantIndex),
		{
			...participantData,
			view: {
				...participantData.view,
				payload: {
					...participantData.view.payload,
					state: gameState.WAITING,
				},
			},
		},
		...state.participants.slice(participantIndex + 1),
	]
	return {
		...state,
		participants,
	}
}

const updateStudentSubmission = (state: IGameStore, action: AnyAction) => {
	const participantIndex = state.participants.findIndex(
		(participant: any) => participant.uid === action.studentSubmission.uid
	)
	const participantData = state.participants[participantIndex]
	const participants = [
		...state.participants.slice(0, participantIndex),
		{
			...participantData,
			view: {
				...participantData.view,
				payload: {
					...participantData.view.payload,
					answersUids: action.studentSubmission.answersUids,
					isGameAnswered: action.studentSubmission.isGameAnswered,
					state: gameState.ANSWERED,
				},
			},
		},
		...state.participants.slice(participantIndex + 1),
	]
	return {
		...state,
		participants,
	}
}

const setCurrentNumberOfCorrectAnswersByParticipantsFromServer = (state: IGameStore, action: any): any => {
	return {
		...state,
		studentsIDWithNumberOfCorrectAnswersFromServer:
			action.payload && action.payload.payload && action.payload.payload.studentsIDWithNumberOfCorrectAnswers,
	}
}

function gameReducer(state: IGameStore = defaultState, action: AnyAction): IGameStore {
	switch (action.type) {
		case actionTypes.GAME_UPDATE:
			return updateGameState(state, action)
		case actionTypes.QUESTION_ANSWER_SELECT:
			return setAnswerAsAnswered(state, action as IActionAnswerSelected)
		case actionTypes.QUESTION_TRANSITION_ENDED:
			return updateUserState(state, action as IActionTransitionEnded)
		case actionTypes.GAME_STUDENT_SUBMISSION_UPDATE:
			return updateStudentSubmission(state, action)
		case actionTypes.SET_CURRENT_NUMBER_OF_CORRECT_ANSWERS_BY_PARTICIPANTS:
			return setCurrentNumberOfCorrectAnswersByParticipantsFromServer(state, action)
		default:
			return state
	}
}

export default gameReducer
